/**
 * ScanReceiptContext — global "scan a receipt from anywhere" provider.
 *
 * Why a context (vs. inline in Transactions.js)?
 *   The existing /transactions page integrates receipt scanning into its
 *   own add-transaction form (pre-populates the inline form). That's
 *   great on desktop. For mobile, the user can be anywhere in the app
 *   (Recurring, Dashboard, Goals) and tap the bottom FAB to log a
 *   receipt without losing their current page. A provider keeps that
 *   ergonomics: anyone can call `openScanner()` and the modal handles
 *   capture → OCR → confirm → save without navigating away.
 *
 * Trust principles:
 *   - Nothing is saved silently. The user always sees a confirm modal
 *     with the parsed fields before anything hits their ledger.
 *   - Save POSTs to the same /api/transactions endpoint as everything
 *     else; receipts are not a special second-class storage path.
 *   - Failures surface as toasts. Mid-OCR the user can cancel.
 */
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import ScanReceiptDialog from '@/components/mobile/ScanReceiptDialog';

const ScanReceiptCtx = createContext(null);

export function ScanReceiptProvider({ children }) {
  // We keep a single hidden file input mounted at the provider level so
  // it can be triggered from anywhere without React strict-mode re-mount
  // surprises. The input has `capture="environment"` which on iOS/Android
  // opens the rear camera, and on desktop falls back to the file picker.
  const fileInputRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);

  const openScanner = useCallback(() => {
    if (fileInputRef.current) {
      // Reset value so picking the same file twice in a row still fires.
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  }, []);

  const closeDialog = useCallback(() => setPendingFile(null), []);

  const onFileChange = useCallback((e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPendingFile(f);
  }, []);

  const value = useMemo(() => ({ openScanner }), [openScanner]);

  return (
    <ScanReceiptCtx.Provider value={value}>
      {children}
      {/* Hidden capture input. `capture="environment"` is a hint to mobile
          browsers to open the back-facing camera. Falls back to gallery /
          file picker if the device lacks a camera. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        capture="environment"
        className="hidden"
        onChange={onFileChange}
        data-testid="global-receipt-file-input"
      />
      {/* Confirmation dialog mounts only while there's a pending file —
          keeps the React tree thin for users who never tap the FAB. */}
      {pendingFile && (
        <ScanReceiptDialog
          file={pendingFile}
          onClose={closeDialog}
        />
      )}
    </ScanReceiptCtx.Provider>
  );
}

export function useScanReceipt() {
  const ctx = useContext(ScanReceiptCtx);
  if (!ctx) {
    // Soft-fail with a no-op so a missing provider doesn't crash callers
    // that try to open the scanner before the shell has mounted.
    return { openScanner: () => { /* no-op */ } };
  }
  return ctx;
}
